import { friendlyMessage } from '../../components/ui/friendlyMessage';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from '../../components/ui/notify';
import {
  publicDeleteAccount,
  publicLookupDeletionEmail,
  publicSendDeletionOtp,
  publicVerifyDeletionOtp,
} from '../../services/public/publicAccountDeletion.service';
import { clearAllLocalLogoutData } from '../../utils/logoutCleanup';



const APP_NAME = process.env.REACT_APP_APPLICATION_NAME || 'LMS';
const RECOVERY_DAYS = 60;
const MIN_BUTTON_LOADER_MS = 1500;

function getCurrentOrganizationUrl() {
  if (typeof window === 'undefined' || !window.location) return '';
  const host = String(window.location.hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return '';
  return window.location.origin;
}

async function goToLogin() {
  try {
    await clearAllLocalLogoutData();
  } catch {
    localStorage.clear();
    sessionStorage.clear();
  }
  window.location.assign('/auth/login');
}

const STEPS = {
  EMAIL: 'email',
  ORG: 'org',
  OTP: 'otp',
  CONFIRM: 'confirm',
  DONE: 'done',
};

async function waitForMinLoader(startTime) {
  const remaining = MIN_BUTTON_LOADER_MS - (Date.now() - startTime);
  if (remaining > 0) {
    await new Promise((resolve) => setTimeout(resolve, remaining));
  }
}

function DeleteAccountButton({ loading, loadingLabel, children, className = '', type = 'button', ...props }) {
  return (
    <button
      type={type}
      className={`tw-inline-flex tw-items-center tw-justify-center tw-gap-[8px] tw-w-[fit-content] tw-min-w-[160px] tw-min-h-[44px] tw-p-[12px_20px] tw-border-0 tw-rounded-[8px] tw-bg-[#0058ff] !tw-text-[#ffffff] tw-text-[15px] tw-font-semibold tw-cursor-pointer tw-no-underline [a&]:tw-inline-flex [a&]:tw-items-center [a&]:tw-justify-center [a&]:tw-gap-[8px] [a&]:tw-w-[fit-content] [a&]:tw-min-w-[160px] [a&]:tw-min-h-[44px] [a&]:tw-p-[12px_20px] [a&]:tw-border-0 [a&]:tw-rounded-[8px] [a&]:tw-bg-[#0058ff] [a&]:!tw-text-[#ffffff] [a&]:tw-text-[15px] [a&]:tw-font-semibold [a&]:tw-cursor-pointer [a&]:tw-no-underline [a&:hover]:!tw-text-[#ffffff] [a&:hover]:tw-no-underline [&:disabled]:tw-opacity-[0.65] [&:disabled]:tw-cursor-not-allowed delete-account-btn   ${className}`.trim()}
      disabled={loading || props.disabled}
      {...props}>
      {loading ? (
        <>
          <span className="tw-w-[16px] tw-h-[16px] tw-border-[2px] tw-border-solid tw-border-[rgba(255,_255,_255,_0.35)] tw-border-t-[#ffffff] tw-rounded-[50%] tw-animate-[delete-account-spin_0.7s_linear_infinite] tw-shrink-[0] delete-account-btn-spinner" aria-hidden="true" />
          <span>{loadingLabel}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

function PublicDeleteAccount() {
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [deletionToken, setDeletionToken] = useState('');
  const [deletionReason, setDeletionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  const organizationUrl = selectedOrg?.organization_url || '';

  const handleLookup = async (event) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');
    const startTime = Date.now();
    let nextStep = null;
    let nextOrganizations = null;
    let nextError = null;

    try {
      const response = await publicLookupDeletionEmail({
        email: trimmed,
        organization_url: getCurrentOrganizationUrl() || undefined,
      });
      const data = response?.data || {};

      if (data.step === 'select_organization' && Array.isArray(data.organizations)) {
        nextStep = STEPS.ORG;
        nextOrganizations = data.organizations;
      } else if (data.organization) {
        setSelectedOrg(data.organization);
        const otpResponse = await publicSendDeletionOtp({
          email: trimmed,
          organization_url: data.organization.organization_url,
        });
        if (otpResponse?.success === false) {
          nextError = otpResponse?.message || 'Failed to send verification code.';
        } else {
          nextStep = STEPS.OTP;
        }
      } else {
        nextError = response?.message || 'Unable to find an account for this email.';
      }
    } catch (err) {
      nextError = err?.message || 'Unable to find an account for this email.';
    } finally {
      await waitForMinLoader(startTime);
      setLoading(false);
      if (nextError) {
        setError(nextError);
      } else if (nextStep === STEPS.ORG) {
        setOrganizations(nextOrganizations);
        setStep(STEPS.ORG);
      } else if (nextStep === STEPS.OTP) {
        toast.success('Verification code sent to your email.');
        setStep(STEPS.OTP);
      }
    }
  };

  const sendOtp = async (emailValue = email.trim(), orgUrl = organizationUrl, { isResend = false } = {}) => {
    const setLoader = isResend ? setResending : setLoading;
    setLoader(true);
    setError('');
    const startTime = Date.now();
    let nextStep = null;
    let nextError = null;

    try {
      const response = await publicSendDeletionOtp({
        email: emailValue,
        organization_url: orgUrl,
      });
      if (response?.success === false) {
        nextError = response?.message || 'Failed to send verification code.';
      } else {
        nextStep = STEPS.OTP;
      }
    } catch (err) {
      nextError = err?.message || 'Failed to send verification code.';
    } finally {
      await waitForMinLoader(startTime);
      setLoader(false);
      if (nextError) {
        setError(nextError);
      } else if (nextStep) {
        toast.success('Verification code sent to your email.');
        setStep(nextStep);
      }
    }
  };

  const handleResendOtp = async () => {
    await sendOtp(email.trim(), organizationUrl, { isResend: true });
  };

  const handleSelectOrg = async (org) => {
    setSelectedOrg(org);
    await sendOtp(email.trim(), org.organization_url);
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    if (!otpCode.trim()) {
      setError('Please enter the verification code.');
      return;
    }

    setLoading(true);
    setError('');
    const startTime = Date.now();
    let nextStep = null;
    let nextToken = null;
    let nextError = null;

    try {
      const response = await publicVerifyDeletionOtp({
        email: email.trim(),
        organization_url: organizationUrl,
        otp_code: otpCode.trim(),
      });
      if (response?.success === false) {
        nextError = response?.message || 'Invalid or expired verification code.';
      } else {
        nextStep = STEPS.CONFIRM;
        nextToken = response?.data?.deletion_token || '';
      }
    } catch (err) {
      nextError = err?.message || 'Invalid or expired verification code.';
    } finally {
      await waitForMinLoader(startTime);
      setLoading(false);
      if (nextError) {
        setError(nextError);
      } else if (nextStep) {
        setDeletionToken(nextToken);
        setStep(nextStep);
      }
    }
  };

  const handleDelete = async (event) => {
    event.preventDefault();
    if (!deletionToken) {
      setError('Verification expired. Please request a new code.');
      setStep(STEPS.OTP);
      return;
    }

    setLoading(true);
    setError('');
    const startTime = Date.now();
    let nextStep = null;
    let nextError = null;

    try {
      const response = await publicDeleteAccount({
        email: email.trim(),
        organization_url: organizationUrl,
        deletion_token: deletionToken,
        deletion_reason: deletionReason.trim() || undefined,
        confirm: true,
      });
      if (response?.success === false) {
        nextError = response?.message || 'Failed to delete account.';
      } else {
        nextStep = STEPS.DONE;
      }
    } catch (err) {
      nextError = err?.message || 'Failed to delete account.';
    } finally {
      await waitForMinLoader(startTime);
      setLoading(false);
      if (nextError) {
        setError(nextError);
      } else if (nextStep) {
        setStep(nextStep);
      }
    }
  };

  return (
    <div className={"tw-min-h-[100vh] tw-bg-[#ffffff] tw-text-[#171717] [font-family:-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] tw-leading-[1.6] [&_.privacy-policy-brand]:tw-bg-[none] [&_.privacy-policy-brand]:tw-border-0 [&_.privacy-policy-brand]:tw-p-0 [&_.privacy-policy-brand]:tw-cursor-pointer [&_.privacy-policy-brand]:[font-family:inherit] privacy-policy-page delete-account-page"}>
      <header className="tw-border-b-[1px] tw-border-solid tw-border-b-[#e5e5e5] tw-bg-[#ffffff] tw-sticky tw-top-0 tw-z-[10] privacy-policy-header">
        <div className="tw-max-w-[800px] tw-m-[0_auto] tw-p-[16px_24px] tw-flex tw-items-center tw-justify-between tw-gap-[16px] tw-flex-wrap privacy-policy-header-inner">
          <button type="button" className={"tw-text-[18px] tw-font-bold tw-text-[#0058ff] tw-no-underline [&:hover]:tw-underline privacy-policy-brand"} onClick={goToLogin}>
            {APP_NAME}
          </button>
          <Link to="/privacy" className="tw-text-[13px] tw-text-[#737373] privacy-policy-updated">
            Privacy Policy
          </Link>
        </div>
      </header>

      <main className={"tw-max-w-[800px] tw-m-[0_auto] tw-p-[40px_24px_64px] [&_h1]:tw-text-[32px] [&_h1]:tw-font-bold [&_h1]:tw-text-[#171717] [&_section]:tw-mb-[32px] [&_h2]:tw-text-[20px] [&_h2]:tw-font-semibold [&_h2]:tw-mb-[12px] [&_h2]:tw-text-[#171717] [&_h3]:tw-text-[16px] [&_h3]:tw-font-semibold [&_h3]:tw-m-[16px_0_8px] [&_h3]:tw-text-[#262626] [&_p]:tw-text-[15px] [&_p]:tw-text-[#404040] [&_p]:tw-mb-[12px] [&_ul]:tw-m-[0_0_12px_20px] [&_ul]:tw-p-0 [&_li]:tw-text-[15px] [&_li]:tw-text-[#404040] [&_li]:tw-mb-[8px] [&_a]:tw-text-[#0058ff] [&_a]:tw-no-underline [&_a:hover]:tw-underline max-[640px]:tw-p-[24px_16px_48px] max-[640px]:[&_h1]:tw-text-[26px] [&_h1]:tw-mb-[12px] privacy-policy-main delete-account-main"}>
        <h1>Delete your account</h1>
        <p className="tw-text-[16px] tw-text-[#404040] tw-mb-[32px] privacy-policy-intro">
          Request deletion of your trainee account without signing in. We will send a one-time
          verification code to your registered email. Your account is soft-deleted immediately and
          may be restored by an administrator within {RECOVERY_DAYS} days.
        </p>

        {error ? <div className="tw-mt-[16px] tw-p-[12px_14px] tw-rounded-[8px] tw-bg-[#fef2f2] tw-border-[1px] tw-border-solid tw-border-[#fecaca] tw-text-[#991b1b] tw-text-[14px] delete-account-error">{friendlyMessage(error)}</div> : null}

        {step === STEPS.EMAIL ? (
          <form className={"tw-mt-[24px] tw-flex tw-flex-col tw-gap-[12px] [&_label]:tw-text-[14px] [&_label]:tw-font-semibold [&_label]:tw-text-[#404040] [&_input]:tw-w-full [&_input]:tw-max-w-[420px] [&_input]:tw-p-[12px_14px] [&_input]:tw-border-[1px] [&_input]:tw-border-solid [&_input]:tw-border-[#d4d4d4] [&_input]:tw-rounded-[8px] [&_input]:tw-text-[15px] [&_textarea]:tw-w-full [&_textarea]:tw-max-w-[420px] [&_textarea]:tw-p-[12px_14px] [&_textarea]:tw-border-[1px] [&_textarea]:tw-border-solid [&_textarea]:tw-border-[#d4d4d4] [&_textarea]:tw-rounded-[8px] [&_textarea]:tw-text-[15px] [&_input:focus]:[outline:2px_solid_#0058ff33] [&_input:focus]:tw-border-[#0058ff] [&_textarea:focus]:[outline:2px_solid_#0058ff33] [&_textarea:focus]:tw-border-[#0058ff] delete-account-form"} onSubmit={handleLookup}>
            <label htmlFor="delete-email">Email address</label>
            <input
              id="delete-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={loading}
            />
            <DeleteAccountButton type="submit" loading={loading} loadingLabel="Checking…">
              Continue
            </DeleteAccountButton>
          </form>
        ) : null}

        {step === STEPS.ORG ? (
          <div className={"tw-mt-[24px] tw-flex tw-flex-col tw-gap-[12px] [&_label]:tw-text-[14px] [&_label]:tw-font-semibold [&_label]:tw-text-[#404040] [&_input]:tw-w-full [&_input]:tw-max-w-[420px] [&_input]:tw-p-[12px_14px] [&_input]:tw-border-[1px] [&_input]:tw-border-solid [&_input]:tw-border-[#d4d4d4] [&_input]:tw-rounded-[8px] [&_input]:tw-text-[15px] [&_textarea]:tw-w-full [&_textarea]:tw-max-w-[420px] [&_textarea]:tw-p-[12px_14px] [&_textarea]:tw-border-[1px] [&_textarea]:tw-border-solid [&_textarea]:tw-border-[#d4d4d4] [&_textarea]:tw-rounded-[8px] [&_textarea]:tw-text-[15px] [&_input:focus]:[outline:2px_solid_#0058ff33] [&_input:focus]:tw-border-[#0058ff] [&_textarea:focus]:[outline:2px_solid_#0058ff33] [&_textarea:focus]:tw-border-[#0058ff] delete-account-form"}>
            <p>Select your organization:</p>
            <div className="tw-flex tw-flex-col tw-gap-[10px] tw-max-w-[480px] delete-account-org-list">
              {organizations.map((org) => (
                <button
                  key={org.organization_id}
                  type="button"
                  className={"tw-flex tw-flex-col tw-items-start tw-gap-[4px] tw-p-[14px_16px] tw-border-[1px] tw-border-solid tw-border-[#e5e5e5] tw-rounded-[10px] tw-bg-[#fafafa] tw-cursor-pointer tw-text-left [&:hover]:tw-border-[#0058ff] [&:hover]:tw-bg-[#f0f7ff] [&_span]:tw-text-[13px] [&_span]:tw-text-[#737373] delete-account-org-card"}
                  disabled={loading}
                  onClick={() => handleSelectOrg(org)}>
                  <strong>{org.name || 'Organization'}</strong>
                  <span>{org.organization_url}</span>
                </button>
              ))}
            </div>
            {loading ? (
              <p className="tw-inline-flex tw-items-center tw-gap-[8px] tw-text-[14px] tw-text-[#525252] delete-account-org-loading">
                <span className="tw-w-[14px] tw-h-[14px] tw-border-[2px] tw-border-solid tw-border-[rgba(0,_88,_255,_0.25)] tw-border-t-[#0058ff] tw-rounded-[50%] tw-animate-[delete-account-spin_0.7s_linear_infinite] delete-account-link-spinner" aria-hidden="true" />
                Sending verification code…
              </p>
            ) : null}
          </div>
        ) : null}

        {step === STEPS.OTP ? (
          <form className={"tw-mt-[24px] tw-flex tw-flex-col tw-gap-[12px] [&_label]:tw-text-[14px] [&_label]:tw-font-semibold [&_label]:tw-text-[#404040] [&_input]:tw-w-full [&_input]:tw-max-w-[420px] [&_input]:tw-p-[12px_14px] [&_input]:tw-border-[1px] [&_input]:tw-border-solid [&_input]:tw-border-[#d4d4d4] [&_input]:tw-rounded-[8px] [&_input]:tw-text-[15px] [&_textarea]:tw-w-full [&_textarea]:tw-max-w-[420px] [&_textarea]:tw-p-[12px_14px] [&_textarea]:tw-border-[1px] [&_textarea]:tw-border-solid [&_textarea]:tw-border-[#d4d4d4] [&_textarea]:tw-rounded-[8px] [&_textarea]:tw-text-[15px] [&_input:focus]:[outline:2px_solid_#0058ff33] [&_input:focus]:tw-border-[#0058ff] [&_textarea:focus]:[outline:2px_solid_#0058ff33] [&_textarea:focus]:tw-border-[#0058ff] delete-account-form"} onSubmit={handleVerifyOtp}>
            <p>
              Verification code sent to <strong>{email}</strong>
            </p>
            <label htmlFor="delete-otp">Verification code</label>
            <input
              id="delete-otp"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="6-digit code"
              disabled={loading || resending}
            />
            <div className="tw-flex tw-items-center tw-gap-[16px] tw-flex-wrap delete-account-actions">
              <DeleteAccountButton type="submit" loading={loading} loadingLabel="Verifying…">
                Verify code
              </DeleteAccountButton>
              <button
                type="button"
                className={"tw-inline-flex tw-items-center tw-gap-[8px] tw-bg-[none] tw-border-0 tw-text-[#0058ff] tw-text-[14px] tw-cursor-pointer tw-p-0 tw-min-h-[44px] [&:disabled]:tw-opacity-[0.65] [&:disabled]:tw-cursor-not-allowed delete-account-link-btn"}
                disabled={loading || resending}
                onClick={handleResendOtp}>
                {resending ? (
                  <>
                    <span className="tw-w-[14px] tw-h-[14px] tw-border-[2px] tw-border-solid tw-border-[rgba(0,_88,_255,_0.25)] tw-border-t-[#0058ff] tw-rounded-[50%] tw-animate-[delete-account-spin_0.7s_linear_infinite] delete-account-link-spinner" aria-hidden="true" />
                    Sending…
                  </>
                ) : (
                  'Resend code'
                )}
              </button>
            </div>
          </form>
        ) : null}

        {step === STEPS.CONFIRM ? (
          <form className={"tw-mt-[24px] tw-flex tw-flex-col tw-gap-[12px] [&_label]:tw-text-[14px] [&_label]:tw-font-semibold [&_label]:tw-text-[#404040] [&_input]:tw-w-full [&_input]:tw-max-w-[420px] [&_input]:tw-p-[12px_14px] [&_input]:tw-border-[1px] [&_input]:tw-border-solid [&_input]:tw-border-[#d4d4d4] [&_input]:tw-rounded-[8px] [&_input]:tw-text-[15px] [&_textarea]:tw-w-full [&_textarea]:tw-max-w-[420px] [&_textarea]:tw-p-[12px_14px] [&_textarea]:tw-border-[1px] [&_textarea]:tw-border-solid [&_textarea]:tw-border-[#d4d4d4] [&_textarea]:tw-rounded-[8px] [&_textarea]:tw-text-[15px] [&_input:focus]:[outline:2px_solid_#0058ff33] [&_input:focus]:tw-border-[#0058ff] [&_textarea:focus]:[outline:2px_solid_#0058ff33] [&_textarea:focus]:tw-border-[#0058ff] delete-account-form"} onSubmit={handleDelete}>
            <div className={"tw-p-[16px] tw-rounded-[8px] tw-bg-[#fef2f2] tw-border-[1px] tw-border-solid tw-border-[#fecaca] tw-text-[#7f1d1d] tw-text-[14px] [&_ul]:tw-m-[12px_0_0] [&_ul]:tw-pl-[20px] delete-account-warning"}>
              <strong>This action cannot be undone.</strong>
              <ul>
                <li>You will be logged out on all devices.</li>
                <li>Your account will be deactivated immediately.</li>
                <li>An administrator may restore it within {RECOVERY_DAYS} days.</li>
                <li>After {RECOVERY_DAYS} days, data may be permanently deleted.</li>
              </ul>
            </div>
            <label htmlFor="delete-reason">Reason for leaving (optional)</label>
            <textarea
              id="delete-reason"
              rows={3}
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
              disabled={loading}
            />
            <DeleteAccountButton
              type="submit"
              className="tw-bg-[#dc2626] delete-account-btn-danger"
              loading={loading}
              loadingLabel="Deleting…">
              Delete my account
            </DeleteAccountButton>
          </form>
        ) : null}

        {step === STEPS.DONE ? (
          <div className={"[&_h2]:tw-text-[22px] [&_h2]:tw-mb-[8px] delete-account-success"}>
            <h2>Account deleted</h2>
            <p>
              Your account has been deactivated. Contact your administrator within {RECOVERY_DAYS}{' '}
              days if you need to restore it.
            </p>
            <button
              type="button"
              className={"tw-inline-flex tw-items-center tw-justify-center tw-gap-[8px] tw-w-[fit-content] tw-min-w-[160px] tw-min-h-[44px] tw-p-[12px_20px] tw-border-0 tw-rounded-[8px] tw-bg-[#0058ff] !tw-text-[#ffffff] tw-text-[15px] tw-font-semibold tw-cursor-pointer tw-no-underline [a&]:tw-inline-flex [a&]:tw-items-center [a&]:tw-justify-center [a&]:tw-gap-[8px] [a&]:tw-w-[fit-content] [a&]:tw-min-w-[160px] [a&]:tw-min-h-[44px] [a&]:tw-p-[12px_20px] [a&]:tw-border-0 [a&]:tw-rounded-[8px] [a&]:tw-bg-[#0058ff] [a&]:!tw-text-[#ffffff] [a&]:tw-text-[15px] [a&]:tw-font-semibold [a&]:tw-cursor-pointer [a&]:tw-no-underline [a&:hover]:!tw-text-[#ffffff] [a&:hover]:tw-no-underline [&:disabled]:tw-opacity-[0.65] [&:disabled]:tw-cursor-not-allowed tw-mt-[8px] delete-account-btn delete-account-btn-inline"}
              onClick={goToLogin}>
              Back to sign in
            </button>
          </div>
        ) : null}
      </main>
    </div>
  );
}

export default PublicDeleteAccount;
